import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ACTIVITY_STATUS, ACTIVITY_STATUS_LABEL, can, type ActivityStatus } from '@rat/shared';
import { fetchActivities, fetchInstitutionalDashboard } from '../lib/queries';
import { useAuth } from '../auth/AuthProvider';
import { ActivityStatusPill, PageHeader, QueryState } from '../components/ui';
import { downloadCsv } from '../lib/csv';

export function ActivitiesList() {
  const { authz } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ActivityStatus | ''>('');

  const q = useQuery({
    queryKey: ['activities', 'all', search, status],
    queryFn: () =>
      fetchActivities({
        search: search || undefined,
        status: status || undefined,
        limit: 100,
      }),
  });

  // Mismo patron que ActivityWizard.tsx: authz.units solo trae las unidades
  // PROPIAS -- un institucional ve actividades de todas, asi que el nombre
  // de unidad para exportar se completa con el tablero institucional cuando
  // aplica.
  const instDash = useQuery({
    queryKey: ['dash', 'inst', 'units-for-export'],
    queryFn: fetchInstitutionalDashboard,
    enabled: authz.institutional,
  });
  const unitNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of authz.units) m.set(u.unit_id, u.name);
    for (const u of instDash.data?.por_unidad ?? []) m.set(u.unit_id, u.name);
    return m;
  }, [authz.units, instDash.data]);

  function exportCsv() {
    const rows = q.data ?? [];
    const header = ['codigo', 'titulo', 'estado', 'completitud_pct', 'unidad', 'sensible', 'transferencia_internacional', 'decision_automatizada', 'modificado'];
    const body = rows.map((a) => [
      a.ref_code,
      a.title,
      ACTIVITY_STATUS_LABEL[a.status],
      String(a.completeness_pct),
      unitNameById.get(a.responsible_unit_id) ?? a.responsible_unit_id,
      a.has_sensitive_data ? 'si' : 'no',
      a.has_international_transfer ? 'si' : 'no',
      a.has_automated_decision ? 'si' : 'no',
      a.updated_at,
    ]);
    downloadCsv(`actividades_rat_${new Date().toISOString().slice(0, 10)}.csv`, [header, ...body]);
  }

  return (
    <>
      <PageHeader
        title="Actividades de tratamiento"
        sub="Solo se listan las actividades de las unidades a las que usted tiene acceso"
        actions={
          can(authz, 'activity.create') && (
            <>
              <Link className="btn btn--ghost" to="/actividades/importar">
                Importar
              </Link>
              <Link className="btn btn--primary" to="/actividades/nueva">
                + Nueva actividad
              </Link>
            </>
          )
        }
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          placeholder="Buscar por nombre o código…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            padding: '8px 11px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--surface)',
            color: 'var(--ink)',
            font: 'inherit',
          }}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as ActivityStatus | '')}
          style={{
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--surface)',
            color: 'var(--ink)',
          }}
        >
          <option value="">Estado: todos</option>
          {ACTIVITY_STATUS.map((s) => (
            <option key={s} value={s}>
              {ACTIVITY_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <button className="btn btn--ghost" disabled={!q.data || q.data.length === 0} onClick={exportCsv}>
          Exportar CSV
        </button>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        <div className="tablewrap">
          <table className="data">
            <thead>
              <tr>
                <th>Código</th>
                <th>Actividad</th>
                <th>Estado</th>
                <th>Compl.</th>
                <th>Atributos</th>
                <th>Modificado</th>
              </tr>
            </thead>
            <tbody>
              {(q.data ?? []).map((a) => (
                <tr key={a.id}>
                  <td className="mono">{a.ref_code}</td>
                  <td>
                    <Link to={`/actividades/${a.id}`}>{a.title || '(sin nombre)'}</Link>
                  </td>
                  <td>
                    <ActivityStatusPill status={a.status} />
                  </td>
                  <td className="mono">{a.completeness_pct}%</td>
                  <td>
                    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {a.has_sensitive_data && <Tag tone="crit">sensible</Tag>}
                      {a.has_international_transfer && <Tag tone="warn">transf. int.</Tag>}
                      {a.has_automated_decision && <Tag tone="info">decisión auto.</Tag>}
                    </span>
                  </td>
                  <td className="mono">{new Date(a.updated_at).toLocaleDateString('es-CL')}</td>
                </tr>
              ))}
              {q.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 24 }}>
                    No hay actividades que coincidan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </QueryState>
    </>
  );
}

function Tag({ tone, children }: { tone: 'crit' | 'warn' | 'info'; children: ReactNode }) {
  const bg = { crit: 'var(--critical-weak)', warn: 'var(--warning-weak)', info: 'var(--info-weak)' }[tone];
  const fg = { crit: 'var(--critical)', warn: 'var(--warning)', info: 'var(--info)' }[tone];
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 2, background: bg, color: fg }}>
      {children}
    </span>
  );
}
